import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, extractErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { OperonLogo } from "@/components/OperonLogo";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [attempts, setAttempts] = useState(0);
  const [tx, setTx] = useState(null);
  const { refreshMe } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async (tryN = 0) => {
      if (cancelled || tryN > 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get("/billing/checkout/status", { params: { session_id: sessionId } });
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshMe();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (e) { /* try again */ }
      setAttempts(tryN + 1);
      setTimeout(() => poll(tryN + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center">
        <Link to="/" className="inline-flex"><OperonLogo theme={theme} size={34} /></Link>
        <div className="mt-6">
          {status === "pending" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
              <h1 className="mt-3 font-heading text-xl font-semibold">Confirming your subscription…</h1>
              <p className="text-sm text-muted-foreground mt-1">This takes a few seconds. Attempt {attempts}/8.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
              <h1 className="mt-3 font-heading text-2xl font-semibold">Subscription active</h1>
              <p className="text-sm text-muted-foreground mt-1">Welcome aboard! Your Operon {tx?.plan} plan is now active.</p>
              <Button className="mt-4" onClick={() => navigate("/app/dashboard")} data-testid="billing-success-continue-button">
                Go to dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </>
          )}
          {(status === "expired" || status === "timeout") && (
            <>
              <XCircle className="w-12 h-12 mx-auto text-destructive" />
              <h1 className="mt-3 font-heading text-xl font-semibold">Payment not completed</h1>
              <p className="text-sm text-muted-foreground mt-1">If you did complete payment, it may still be processing. Check billing page shortly.</p>
              <Button className="mt-4" onClick={() => navigate("/app/billing")}>Back to billing</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
